import type {
    UpdateWorkOrderOtherPriceItemInput,
    UpdateWorkOrderOtherSeasonPriceItemInput,
    WorkOrderOtherCostItem,
    WorkOrderOtherObjectInput,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type {
    ICheckedObjs,
    IFormValues,
    IOtherNoteValues,
    IOtherValues,
} from 'pages/Project/Common/AddWorkOrderOtherForm/helpers';
import { replacerCost } from 'utils/replacerCost';

export const prepareOtherCostData = ({
    checkedObjects,
    priceValues,
    priceNotes,
    workOrderStatus,
}: {
    checkedObjects: ICheckedObjs;
    priceValues: IOtherValues;
    priceNotes?: IOtherNoteValues;
    workOrderStatus: WorkOrderStatusEnum | undefined;
}): UpdateWorkOrderOtherPriceItemInput[] =>
    Object.entries(checkedObjects).reduce(
        (acc: UpdateWorkOrderOtherPriceItemInput[], [otherCostId, otherCostValue]) => {
            // Если не выбрана прочая, не формируем объект для сезонов
            if (!otherCostValue.parent) {
                return acc;
            }

            // Список айдишек выбранных объектов
            const otherObjectList =
                otherCostValue &&
                Object.entries(otherCostValue).reduce((objListAcc: WorkOrderOtherObjectInput[], [objId, objValue]) => {
                    if (objValue && objId !== 'parent') {
                        if (priceNotes?.[otherCostId]?.[objId]) {
                            objListAcc.push({
                                objectId: Number(objId),
                                rationale: priceNotes[otherCostId][objId],
                            });
                        } else {
                            objListAcc.push({
                                objectId: Number(objId),
                            });
                        }
                    }

                    return objListAcc;
                }, []);

            // Список значений для сезонов
            const seasonsList =
                priceValues?.[otherCostId] &&
                Object.entries(priceValues[otherCostId]).reduce(
                    (seasonsListAcc: UpdateWorkOrderOtherSeasonPriceItemInput[], [seasonId, seasonValue]) => {
                        // seasonId - season_ID
                        if (seasonValue) {
                            const seasonNumberId = Number(seasonId.split('_')[1]);

                            seasonsListAcc.push({
                                seasonId: Number(seasonNumberId),
                                ...(seasonValue?.includedCost
                                    ? { includedCost: replacerCost(seasonValue.includedCost) }
                                    : { isIncluded: seasonValue.isIncluded }),
                            });
                        }

                        return seasonsListAcc;
                    },
                    [],
                );

            if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                acc.push({
                    otherCostId: Number(otherCostId),
                    otherCostObjects: otherObjectList,
                });
            }
            if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                acc.push({
                    otherCostId: Number(otherCostId),
                    otherCostObjects: otherObjectList,
                    otherSeasonPrices: seasonsList ?? [],
                });
            }

            return acc;
        },
        [],
    );

export const getInitialState = (data: Partial<WorkOrderOtherCostItem>[]) =>
    data?.reduce(
        (acc: IFormValues, item) => {
            const objCheckedState = item?.workOrderObjects?.reduce((objAcc: Record<any, any>, o) => {
                objAcc[`${o.workOrderRootObjectId}`] = Boolean(o?.isIncluded);

                return objAcc;
            }, {});
            const allObjectsChecked = item?.workOrderObjects?.every((o) => o.isIncluded);
            const someObjectsChecked = item?.workOrderObjects?.some((o) => o.isIncluded);
            // eslint-disable-next-line
            const parentState = allObjectsChecked ? true : someObjectsChecked ? 'intermediate' : false;

            const otherSeasonPriceValues = item?.seasonLimits?.reduce((seasonAcc: Record<any, any>, season) => {
                seasonAcc[`season_${season.workOrderSeasonId}`] =
                    item?.otherCost?.calculatingType === OtherCostCalculationTypeEnum.Percent
                        ? {
                              isIncluded: season?.isIncluded,
                          }
                        : {
                              includedCost: season?.includedCost,
                          };

                return seasonAcc;
            }, {});

            const notes = item?.workOrderObjects?.reduce((notesAcc: Record<any, any>, o) => {
                notesAcc[o.workOrderRootObjectId] = o?.rationale;

                return notesAcc;
            }, {});

            acc.otherPriceNotes = {
                ...acc.otherPriceNotes,
                [item?.otherCost?.contractOtherCostId]: {
                    ...notes,
                },
            };

            acc.checkedIds = {
                ...acc.checkedIds,
                [item?.otherCost?.contractOtherCostId]: {
                    parent: parentState,
                    ...objCheckedState,
                },
            };

            acc.otherPriceValues = {
                ...acc.otherPriceValues,
                [item?.otherCost?.contractOtherCostId]: {
                    ...otherSeasonPriceValues,
                },
            };

            return acc;
        },
        { otherPriceValues: {}, checkedIds: {}, otherPriceNotes: {} },
    );
